import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMe } from '../contexts/me-context';
import { useSecureState } from '../contexts/secure-state-context';
import { useSnackbar } from '../contexts/snackbar-provider';
import { useSession } from '../hooks/useSession';

/**
 * Custom hook to monitor session state and handle automatic logout
 * when session expires or user is logged out from another app
 */
export const useSessionMonitor = () => {
  const navigate = useNavigate();
  const userSession = useSession();
  const { isAuthenticated } = userSession;
  const { isSecure, updateIsSecure } = useSecureState();
  const { updateMe } = useMe();
  const enqueueSnackbar = useSnackbar();
  const { pathname, search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const redirectWithIntent = useMemo(
        () => {
          const safeIntent = (`${pathname}`.trim());
          searchParams.append("intent", safeIntent);
          const qp = searchParams.toString();
          const redirectWithIntent = `/login/?${qp}`;
  
          return redirectWithIntent;
        },
        [pathname, search],
      );

  /**
   * EFFECT 1: Cross-tab logout detection
   * Detects when user logs out from another tab by checking localStorage
   * This provides immediate cross-tab logout detection (within 1-2 seconds)
   */
  useEffect(() => {
    if (!isSecure) return;

    const checkCrossTabLogout = () => {
      const currentSession = localStorage.getItem('session');
      if (!currentSession) {
        console.log('Cross-tab logout detected - session cleared in another tab');
        
        
        // Clear local app state
        updateIsSecure(false);
        updateMe(null);
        userSession.logout(redirectWithIntent);
        
        // Show notification
        enqueueSnackbar('You have been logged out from another tab.', 'warning');
        
        // Redirect to login page
        navigate(redirectWithIntent, { replace: true });
      }
    };

    // Check every 1 second for cross-tab logout
    const interval = setInterval(checkCrossTabLogout, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isSecure, updateIsSecure, updateMe, userSession, enqueueSnackbar, navigate]);

  /**
   * EFFECT 2: Server-side session expiry detection
   * Validates session with the server every 5 minutes to catch server-side session expiration
   * This ensures we detect session expiry even if localStorage still has the session
   */
  useEffect(() => {
    if (!isAuthenticated || !isSecure) return;

    const validateServerSession = async () => {
      try {
        // Make a request to the API to validate the session
        const response = await fetch('/api/v2/me', {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          // Session is invalid on the server
          console.log('Server-side session expired');
          
          updateIsSecure(false);
          updateMe(null);
         userSession.logout(redirectWithIntent);
          enqueueSnackbar('Your session has expired. Please log in again.', 'warning');
           // Redirect to login page
          navigate(redirectWithIntent, { replace: true });
        }
      } catch (error) {
        console.error('Session validation error:', error);
        // On error, assume session is still valid to avoid false logouts
      }
    };

    // Validate session every 5 minutes
    const interval = setInterval(validateServerSession, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, isSecure, updateIsSecure, updateMe, userSession, enqueueSnackbar, navigate]);

  return {
    isAuthenticated,
    isSecure,
  };
};